import * as React from 'react'
import { useForm } from '@tanstack/react-form'
import { toast } from 'sonner'
import { Plus, Minus, CreditCard } from 'lucide-react'
import { create } from '@bufbuild/protobuf'

import type { Item, TaxRate } from '@/gen/cortado/v1/invoices_pb'
import { ItemUnitEnum } from '@/gen/cortado/v1/settings_pb'
import { GetStripeAccountLinkURLRequestSchema } from '@/gen/cortado/v1/payments_pb'
import { formatEnumName } from '@/lib/enum-utils'
import { formatCurrency, formatTaxRate, parseCurrency } from '@/lib/currency-utils'
import { calculateLineTotalCents } from '@/lib/line-item-calculations'
import { useSystemSettings } from '@/stores/settings.store'
import { useCurrentOrganization } from '@/stores/organization.store'
import { useStripeConnection, useGetStripeAccountLinkURL } from '@/stores/stripe.store'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RichTextEditor } from '@/components/shared/rich-text-editor'
import { useRichTextEditor } from '@/hooks/use-rich-text-editor'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'

import type { QuoteLineItem } from './quote-line-items-table'

// Helper function to calculate sales tax for a line item
function calculateLineItemTax(lineTotalCents: number, taxRate: number, isTaxable: boolean): number {
  if (!isTaxable || !taxRate) return 0
  return Math.round(lineTotalCents * taxRate)
}

function isValidQuantity(quantity: number, unit: ItemUnitEnum): boolean {
  return Number.isFinite(quantity) && (unit === ItemUnitEnum.ITEM_UNIT_PER_MILE ? quantity > 0 : quantity >= 1)
}

function normalizeQuantityInput(value: string, unit: ItemUnitEnum): number {
  const minimum = unit === ItemUnitEnum.ITEM_UNIT_PER_MILE ? 0.1 : 1
  const parsed = unit === ItemUnitEnum.ITEM_UNIT_PER_MILE
    ? Number.parseFloat(value)
    : Number.parseInt(value, 10)
  return Number.isFinite(parsed) ? Math.max(minimum, parsed) : minimum
}

function adjustQuantity(quantity: number, delta: number): number {
  return Math.round((quantity + delta) * 10) / 10
}

function UnitPriceInput({
  value,
  onChange,
  disabled,
  disabledReason,
}: {
  value: number
  onChange: (cents: number) => void
  disabled?: boolean
  disabledReason?: string
}) {
  const [displayValue, setDisplayValue] = React.useState(() => formatCurrency(value))
  const internalCents = React.useRef(value)

  // Only sync display when value changes externally (e.g., form reset), not from our own onChange
  React.useEffect(() => {
    if (value !== internalCents.current) {
      setDisplayValue(formatCurrency(value))
      internalCents.current = value
    }
  }, [value])

  const inputElement = (
    <div className="flex items-center">
      <span className="text-sm text-muted-foreground mr-2">$</span>
      <Input
        id="unit-price"
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={(e) => {
          setDisplayValue(e.target.value)
          const cents = parseCurrency(e.target.value)
          internalCents.current = cents
          onChange(cents)
        }}
        onBlur={() => {
          const cents = parseCurrency(displayValue)
          internalCents.current = cents
          setDisplayValue(formatCurrency(cents))
        }}
        placeholder="0.00"
        disabled={disabled}
        required
      />
    </div>
  )

  return (
    <div className="grid gap-3">
      <Label htmlFor="unit-price">
        Unit Price <span className="text-destructive">*</span>
      </Label>
      {disabledReason ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0}>{inputElement}</span>
          </TooltipTrigger>
          <TooltipContent>{disabledReason}</TooltipContent>
        </Tooltip>
      ) : (
        inputElement
      )}
      {disabledReason && (
        <p className="text-xs text-muted-foreground">{disabledReason}</p>
      )}
    </div>
  )
}

interface QuoteLineItemEditSheetProps {
  item: QuoteLineItem | null
  availableItems: Item[]
  availableTaxRates: TaxRate[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (item: QuoteLineItem) => void
  disabled?: boolean
}

export function QuoteLineItemEditSheet({
  item,
  availableItems: _availableItems,
  availableTaxRates,
  open,
  onOpenChange,
  onSave,
  disabled = false,
}: QuoteLineItemEditSheetProps) {
  const { settings } = useSystemSettings()
  const itemUnits = settings?.itemUnits || []
  const isTransportation = item?.unit === ItemUnitEnum.ITEM_UNIT_PER_MILE
  const quantityIncrement = isTransportation ? 0.1 : 1
  const availableUnits = isTransportation
    ? [ItemUnitEnum.ITEM_UNIT_PER_MILE]
    : itemUnits.filter((unit) => unit !== ItemUnitEnum.ITEM_UNIT_UNKNOWN && unit !== ItemUnitEnum.ITEM_UNIT_PER_MILE)

  const { currentOrganization } = useCurrentOrganization()
  const organizationId = currentOrganization?.id || ''
  const { isConnected: isStripeConnected, hasLoaded: stripeHasLoaded } =
    useStripeConnection(organizationId)
  const stripeBlocksPriceEdit = stripeHasLoaded && !isStripeConnected
  const getStripeAccountLink = useGetStripeAccountLinkURL(organizationId)

  const handleConnectStripe = () => {
    if (!organizationId) return
    getStripeAccountLink.mutate(
      create(GetStripeAccountLinkURLRequestSchema, { organizationId }),
      {
        onError: (error) => {
          toast.error(error.message || 'Failed to connect Stripe')
        },
      }
    )
  }

  const isEditMode = !!item?.id || !!item?.tempId || !!item?.sourceItemId
  const isCreateMode = !isEditMode

  // Setup form with default values
  const form = useForm({
    defaultValues: {
      name: item?.name ?? '',
      description: item?.description ?? '',
      unitPriceCents: item?.unitPriceCents ?? 0,
      quantity: item?.quantity ?? 1,
      unit: item?.unit ?? ItemUnitEnum.ITEM_UNIT_PER_EVENT,
      isTaxable: item?.isTaxable ?? true,
      salesTaxRateId: item?.salesTaxRateId ?? undefined,
      salesTaxRateName: item?.salesTaxRateName ?? undefined,
      salesTaxRate: item?.salesTaxRate ?? undefined,
    },
    onSubmit: async ({ value }) => {
      // Validate required fields
      if (!value.name.trim()) {
        toast.error('Item name is required')
        return
      }

      if (!isValidQuantity(value.quantity, value.unit)) {
        toast.error(isTransportation ? 'Mileage must be greater than zero' : 'Quantity must be at least 1')
        return
      }

      if (value.unitPriceCents < 0) {
        toast.error('Price cannot be negative')
        return
      }

      // Calculate line total and tax
      const lineTotalCents = calculateLineTotalCents(value.quantity, value.unitPriceCents)
      const salesTaxCents = calculateLineItemTax(lineTotalCents, value.salesTaxRate || 0, value.isTaxable)

      const updatedItem: QuoteLineItem = {
        ...item,
        id: item?.id,
        sourceItemId: item?.sourceItemId,
        tempId: item?.tempId || (isCreateMode ? `custom-${Date.now()}-${Math.random()}` : undefined),
        name: value.name,
        description: value.description,
        unitPriceCents: value.unitPriceCents,
        quantity: value.quantity,
        unit: value.unit,
        sortOrder: item?.sortOrder ?? 0,
        isTaxable: value.isTaxable,
        salesTaxRateId: value.salesTaxRateId,
        salesTaxRateName: value.salesTaxRateName,
        salesTaxRate: value.salesTaxRate,
        salesTaxCents,
        lineTotalCents,
      }

      onSave(updatedItem)
      toast.success(isCreateMode ? 'Line item added successfully' : 'Line item updated successfully')
      onOpenChange(false)
      form.reset()
    },
  })

  const descriptionEditor = useRichTextEditor({
    initialContent: item?.description ?? '',
    placeholder: 'Enter item description (optional)',
    onUpdate: (html) => {
      form.setFieldValue('description', html)
    },
  })

  React.useEffect(() => {
    descriptionEditor?.setEditable(!disabled)
  }, [descriptionEditor, disabled])

  // Reset form when item changes
  React.useEffect(() => {
    if (item) {
      form.reset({
        name: item.name ?? '',
        description: item.description ?? '',
        unitPriceCents: item.unitPriceCents ?? 0,
        quantity: item.quantity ?? 1,
        unit: item.unit ?? ItemUnitEnum.ITEM_UNIT_PER_EVENT,
        isTaxable: item.isTaxable ?? true,
        salesTaxRateId: item.salesTaxRateId ?? undefined,
        salesTaxRateName: item.salesTaxRateName ?? undefined,
        salesTaxRate: item.salesTaxRate ?? undefined,
      })
      descriptionEditor?.commands.setContent(item.description ?? '')
    }
  }, [item, form, descriptionEditor])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{isEditMode ? 'Edit Line Item' : 'Add Line Item'}</SheetTitle>
          <SheetDescription>
            {isEditMode
              ? 'Update the line item details. Click save when you\'re done.'
              : 'Add a new line item to the quote.'}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          <form
            id="quote-line-item-form"
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="grid auto-rows-min gap-6"
          >
          {/* Item Name */}
          <form.Field name="name">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="item-name">
                  Item Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="item-name"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Enter item name"
                  disabled={disabled}
                  required
                />
              </div>
            )}
          </form.Field>

          {/* Description */}
          <div className="grid gap-3">
            <Label htmlFor="item-description">Description</Label>
            <RichTextEditor editor={descriptionEditor} disabled={disabled} />
          </div>

          {/* Stripe connection alert (informational, gates price editing) */}
          {stripeBlocksPriceEdit && (
            <Alert>
              <CreditCard className="h-4 w-4" />
              <AlertTitle>Connect Stripe to add prices</AlertTitle>
              <AlertDescription>
                <p>You can still draft this line item, but you'll need a connected Stripe account to set a price.</p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleConnectStripe}
                  disabled={getStripeAccountLink.isPending || !organizationId}
                >
                  {getStripeAccountLink.isPending ? 'Connecting...' : 'Connect Stripe'}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Unit Price */}
          <form.Field name="unitPriceCents">
            {(field) => (
              <UnitPriceInput
                value={field.state.value}
                onChange={(cents) => field.handleChange(cents)}
                disabled={disabled || stripeBlocksPriceEdit}
                disabledReason={
                  stripeBlocksPriceEdit
                    ? 'Connect Stripe to set a unit price.'
                    : undefined
                }
              />
            )}
          </form.Field>

          {/* Quantity and Unit */}
          <div className="grid grid-cols-2 gap-4">
            <form.Field name="quantity">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="quantity">
                    Quantity <span className="text-destructive">*</span>
                  </Label>
                  <div className="flex items-center rounded-md border border-input shadow-xs overflow-hidden">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-none border-r border-input"
                      onClick={() => {
                        const newValue = Math.max(quantityIncrement, adjustQuantity(field.state.value, -quantityIncrement))
                        field.handleChange(newValue)
                      }}
                      disabled={disabled || field.state.value <= quantityIncrement}
                    >
                      <Minus className="h-4 w-4" />
                      <span className="sr-only">Decrease</span>
                    </Button>
                    <Input
                      id="quantity"
                      type="number"
                      value={field.state.value}
                      onChange={(e) => {
                        field.handleChange(normalizeQuantityInput(e.target.value, form.state.values.unit))
                      }}
                      onBlur={(e) => {
                        field.handleChange(normalizeQuantityInput(e.target.value, form.state.values.unit))
                      }}
                      min={quantityIncrement}
                      step={quantityIncrement}
                      disabled={disabled}
                      className="border-0 rounded-none text-center text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-none border-l border-input"
                      onClick={() => {
                        field.handleChange(adjustQuantity(field.state.value, quantityIncrement))
                      }}
                      disabled={disabled}
                    >
                      <Plus className="h-4 w-4" />
                      <span className="sr-only">Increase</span>
                    </Button>
                  </div>
                </div>
              )}
            </form.Field>

            <form.Field name="unit">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="unit">
                    Unit <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={field.state.value.toString()}
                    onValueChange={(value) => field.handleChange(parseInt(value) as ItemUnitEnum)}
                    disabled={disabled || isTransportation}
                  >
                    <SelectTrigger id="unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableUnits.map((unit) => (
                        <SelectItem key={unit} value={unit.toString()}>
                          {formatEnumName(unit, ItemUnitEnum)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
          </div>

          {/* Sales Tax Rate */}
          <form.Field name="salesTaxRateId">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="tax-rate">Sales Tax Rate</Label>
                <Select
                  value={field.state.value || 'no-tax'}
                  onValueChange={(value) => {
                    if (value === 'no-tax') {
                      field.handleChange(undefined)
                      form.setFieldValue('isTaxable', false)
                      form.setFieldValue('salesTaxRateName', undefined)
                      form.setFieldValue('salesTaxRate', undefined)
                    } else {
                      const selectedRate = availableTaxRates.find((r) => r.id === value)
                      field.handleChange(value)
                      form.setFieldValue('isTaxable', true)
                      form.setFieldValue('salesTaxRateName', selectedRate?.name)
                      form.setFieldValue('salesTaxRate', selectedRate ? selectedRate.percentage / 100 : 0)
                    }
                  }}
                  disabled={disabled}
                >
                  <SelectTrigger id="tax-rate">
                    <SelectValue placeholder="Select tax rate" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-tax">No Tax</SelectItem>
                    {availableTaxRates.map((rate) => (
                      <SelectItem key={rate.id} value={rate.id}>
                        {rate.name} ({formatTaxRate(rate.percentage)}%)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          {/* Total Preview — subscribed so it recalculates live on every field change */}
          <form.Subscribe
            selector={(s) => ({
              quantity: s.values.quantity,
              unitPriceCents: s.values.unitPriceCents,
              salesTaxRate: s.values.salesTaxRate,
              isTaxable: s.values.isTaxable,
              salesTaxRateName: s.values.salesTaxRateName,
            })}
          >
            {({ quantity, unitPriceCents, salesTaxRate, isTaxable, salesTaxRateName }) => {
              const lineTotalCents = calculateLineTotalCents(quantity, unitPriceCents)
              const salesTaxCents = calculateLineItemTax(lineTotalCents, salesTaxRate || 0, isTaxable)
              const totalWithTax = lineTotalCents + salesTaxCents

              return (
                <div className="rounded-lg border p-4 space-y-2 bg-muted/50">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Line Total</span>
                    <span className="font-medium">${formatCurrency(lineTotalCents)}</span>
                  </div>
                  {isTaxable && salesTaxRate && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Tax ({salesTaxRateName})</span>
                      <span className="font-medium">${formatCurrency(salesTaxCents)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-semibold">Total</span>
                    <span className="font-bold">${formatCurrency(totalWithTax)}</span>
                  </div>
                </div>
              )
            }}
          </form.Subscribe>

          </form>
        </SheetBody>
        <SheetFooter>
          <SheetClose asChild>
            <Button type="button" variant="outline" disabled={disabled}>
              Cancel
            </Button>
          </SheetClose>
          <form.Subscribe
            selector={(s) =>
              [s.values.name, s.values.unitPriceCents, s.values.quantity, s.values.unit, s.isSubmitting] as const
            }
          >
            {([name, unitPriceCents, quantity, unit, isSubmitting]) => (
              <Button
                type="submit"
                form="quote-line-item-form"
                disabled={
                  disabled ||
                  isSubmitting ||
                  !name.trim() ||
                  unitPriceCents < 0 ||
                  !isValidQuantity(quantity, unit) ||
                  !unit
                }
              >
                {disabled ? 'Saving...' : isEditMode ? 'Save Changes' : 'Add Item'}
              </Button>
            )}
          </form.Subscribe>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
